<template>
  <div>
    <template v-if="hasFetched">
      <template v-if="people.items?.length > 0">
        <table class="component">
          <thead class="head">
            <tr>
              <th
                v-for="col in columns"
                :key="col.id"
                class="col-head"
                :class="{
                  [`cell-${col.id}`]: col.id,
                  'is-active': col.id === sort,
                  'is-sortable': col.sortable,
                  'is-loading': loading,
                }"
              >
                <span
                  class="col-head-button"
                  @click="emit('onColumnSort', col.sortable, col.id, col.defaultDir)"
                >
                  <span class="col-head-label">
                    {{ col.label }}
                  </span>
                  <template
                    v-if="col.sortable"
                  >
                    <Icon
                      v-if="loading"
                      class="col-head-icon"
                      icon="fas fa-sync fa-spin"
                    />
                    <Icon
                      v-else
                      class="col-head-icon"
                      :icon="
                        col.id === sort ?
                          (dir === 'asc' ?
                            'fas fa-arrow-down' :
                            'fas fa-arrow-up') :
                          (col.defaultDir === 'asc' ?
                            'fas fa-arrow-down' :
                            'fas fa-arrow-up')
                      "
                    />
                  </template>
                </span>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-if="loading"
              class="row"
            >
              <td
                v-for="(col, index) in columns"
                :key="col.id"
                class="cell"
                :class="{
                  [`cell-${col.id}`]: col.id,
                }"
              >
                <LoaderWithText
                  v-if="index === 0"
                  class="cell-loader"
                >
                  Loading…
                </LoaderWithText>
              </td>
            </tr>
            <template v-else>
              <tr
                v-for="(person) in people.items"
                :key="person.external_id"
                class="row"
              >
                <td class="cell cell-name">
                  <TextLink
                    class="person-link"
                    :to="entityDetailLink(person.external_id)"
                    no-underline
                  >
                    <span class="person-name">{{ person.name }}</span>
                    <span class="person-email">{{ person.email }}</span>
                  </TextLink>
                </td>
                <td class="cell cell-type">
                  <router-link
                    class="cell-link"
                    :to="entityDetailLink(person.external_id)"
                  >
                    <Badge
                      :status="typeBadge(person.entity_type)"
                      resizable
                    >
                      {{ person.entity_type }}
                    </Badge>
                  </router-link>
                </td>
                <td class="cell cell-parents">
                  <div class="cell-content">
                    <router-link
                      v-for="parent in person.parents"
                      :key="parent.external_id"
                      :to="entityDetailLink(parent.external_id)"
                      class="parent-link"
                    >
                      {{ parent.name }}
                    </router-link>
                  </div>
                </td>
                <td class="cell cell-children">
                  <div class="cell-content">
                    <router-link
                      v-for="parent in person.children"
                      :key="parent.external_id"
                      :to="entityDetailLink(parent.external_id)"
                      class="parent-link"
                    >
                      {{ parent.name }}
                    </router-link>
                  </div>
                </td>
                <td class="cell cell-owner">
                  <router-link
                    class="cell-link"
                    :to="entityDetailLink(person.external_id)"
                  >
                    <template v-if="currentAdvisorId && person.advisor_ids">
                      <Badge
                        v-if="person.advisor_ids.includes(currentAdvisorId)"
                        resizable
                        status="primary"
                      >
                        Me
                      </Badge>
                      <Badge
                        v-else
                        resizable
                        status="warning"
                      >
                        Shared with me
                      </Badge>
                    </template>
                  </router-link>
                </td>
                <td class="cell cell-integrations">
                  <router-link
                    class="cell-link"
                    :to="entityDetailLink(person.external_id)"
                  >
                    <Badges
                      v-if="person.crm_type || (person.synced_custodians && person.synced_custodians.length > 0)"
                      gap="2"
                    >
                      <Badge
                        v-if="person.crm_type"
                        status="danger"
                        resizable
                      >
                        {{ person.crm_type }}
                      </Badge>
                      <template v-if="person.synced_custodians && person.synced_custodians.length > 0">
                        <Badge
                          v-for="custodian in person.synced_custodians"
                          :key="custodian"
                          status="warning"
                          resizable
                        >
                          {{ custodian }}
                        </Badge>
                      </template>
                    </Badges>
                    <template v-else>
                      -
                    </template>
                  </router-link>
                </td>
                <!-- <td class="cell cell-alerts">
                  <router-link class="cell-link" :to="entityDetailLink(person.external_id)">
                      -
                  </router-link>
                </td> -->
                <td class="cell cell-created_at">
                  <router-link
                    class="cell-link"
                    :to="entityDetailLink(person.external_id)"
                  >
                    {{ formatDate(person.created_at) }}
                  </router-link>
                </td>
                <td class="cell cell-updated_at">
                  <router-link
                    class="cell-link"
                    :to="entityDetailLink(person.external_id)"
                  >
                    {{ formatDate(person.updated_at) }}
                  </router-link>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div
          v-if="!loading"
          class="stk-p-6"
        >
          <Pagination
            :total-pages="people.totalPages"
            :current-page="people.page"
            :has-previous="people.hasPrevious"
            :has-next="people.hasNext"
            size="3_5"
            @onPrevious="emit('onPrevious')"
            @onNext="emit('onNext')"
            @onPageClick="data => emit('onPageClick', data)"
          />
        </div>
      </template>

      <div
        v-else
        class="empty"
      >
        <Callout
          class="empty-callout"
          status="primary"
        >
          <p>No person was found using that query</p>
          <Button
            variant="primary"
            @click="emit('onReset')"
          >
            Reset the search
          </Button>
        </Callout>
      </div>
    </template>

    <LoaderWithText
      v-else
      class="stk-p-6"
    >
      Loading…
    </LoaderWithText>
    <!-- <StakeModal v-model="showCrmLinkingModal">
      <CrmLinkingModal
        v-if="showCrmLinkingModal"
        :entity-id="entityExternalId"
        message="Please select a CRM contact to link to this entity."
        @contact-linked="handleContactLinked"
        @close="showCrmLinkingModal = false"
      />
    </StakeModal> -->
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import { formatDate } from '@frontend/shared/Util.js';
import { Badge, Badges, Button, Callout, Icon, LoaderWithText, Pagination, TextLink } from '@stakeui';
import { useAdvisorStore } from '@stores/advisorStore.js';

const advisorStore = useAdvisorStore();

const emit = defineEmits(['onPrevious', 'onNext', 'onReset', 'onColumnSort']);

defineProps({
  loading: {
    type: Boolean,
    required: false,
  },
  hasFetched: {
    type: Boolean,
    required: false,
  },
  people: {
    type: Object,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
  sort: {
    type: String,
    required: true,
  },
  dir: {
    type: String,
    required: true,
  },
});

const TYPE_BADGES = {
  'Prospect': 'info',
  'Client': 'success',
};

const typeBadge = type => {
  if (type in TYPE_BADGES) {
    return TYPE_BADGES[type];
  }

  return '';
};

const currentAdvisorId = computed(() => advisorStore.advisor?.id ?? null);

const entityDetailLink = externalId => ({
  name: 'entity-detail',
  params: { entityExternalId: externalId },
});
</script>

<style scoped>
.component {
  border-bottom-style: solid;
  border-bottom-width: 1px;
  border-bottom-color: var(--stk-text-85);
  width: 100%;
}

.head,
.foot {
  background-color: hsl(var(--stk-text-h) var(--stk-text-s) 98%);
}

.head {
  border-top: 1px solid var(--stk-text-85);
}

.foot {
  border-top: 1px solid var(--stk-text-90);
}

.col-head:first-child .col-head-button,
.cell:first-child {
  padding-left: 1.5rem;
}

.col-head:last-child .col-head-button,
.cell:last-child {
  padding-right: 1.5rem;
}

.col-head {
  color: var(--stk-text-60);
  font-weight: 500;
  font-size: 0.75em;
  padding: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;

  &.is-sortable {
    color: var(--stk-primary);

    &:hover {
      color: var(--stk-primary-40);

      .col-head-icon {
        opacity: 1;
      }
    }

    .col-head-button {
      cursor: pointer;
    }
  }

  &.is-active {
    background-color: var(--stk-primary-90);
    border-top: 1px solid var(--stk-primary-50);
    color: var(--stk-primary-40);

    .col-head-icon {
      opacity: 1;
    }
  }

  &.is-loading {
    .col-head-icon {
      opacity: 0;
    }

    &:hover {
      .col-head-icon {
        opacity: 1;
      }
    }
  }
}

.col-head-button {
  align-items: center;
  display: flex;
  padding: 0.25rem 0.75rem;
}

.col-head-label {
  flex-grow: 1;
}

.col-head-icon {
  opacity: 0;
}

.row {
  border-top-style: solid;
  border-top-width: 1px;
  border-top-color: var(--stk-text-90);
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  &:hover {
    background-color: var(--stk-background);

    .person-name::after {
      transform: none;
    }
  }
}

.cell {
  vertical-align: middle;
}

.cell-link {
  color: inherit;
  display: flex;
}

.cell-link,
.cell-content {
  padding: 0.25rem 0.75rem;
}

.none {
  font-style: italic;
  color: var(--stk-text-weak);
}

.cell-name {
  min-width: 12rem;
  width: auto;
  padding: 0 !important;
}

.cell-entity_type {
  width: 9rem;
}

.cell-integrations {
  width: 12rem;
}

td.cell-integrations {
  font-size: 0.875em;
}

td.cell-name {
  font-weight: 600;
}

.person-link {
  display: flex;
  padding: 0.75rem 1rem 0.75rem 1.5rem;
}

.person-name {
  color: var(--stk-text-strong);
  position: relative;

  &::after {
    background-color: currentColor;
    content: "";
    height: 1px;
    position: absolute;
    top: 100%;
    left: 0;
    transition-duration: calc(2 * var(--stk-transition-duration));
    transition-property: transform;
    transform: scaleX(0);
    transform-origin: left center;
    width: 100%;
  }
}

.person-email {
  display: none;
}

.cell-values {
  width: 6rem;
}

.cell-type {
  font-size: 0.875em;
  width: 8rem;
}

.cell-parents,
.cell-children {
  font-size: 0.75em;
  width: 8rem;
}

.parent-link {
  display: block;
  line-height: 1.125;
}

.cell-owner {
  width: 10rem;
}

td.cell-owner {
  font-size: 0.875em;
}

.cell-questionnaires {
  padding-right: 0;
  width: 12rem;
}

td.cell-questionnaires {
  font-size: 0.75em;
}

.questionnaire-badge {
  display: grid;
  align-items: center;
  grid-template-columns: 10em 3em;

  &:not(:last-child) {
    margin-bottom: 0.125em;
  }

  :deep(.stk-badge-body) {
    font-size: inherit;
  }
}

.cell-created_at,
.cell-updated_at {
  width: 8rem;
}

td.cell-created_at,
td.cell-updated_at {
  color: var(--stk-text-normal);
  font-size: 0.75em;
}

.empty {
  padding: 2em;
  display: flex;
  justify-content: center;
  align-items: center;
}

.empty-callout {
  padding: 2em;
  width: 24em;
  justify-content: center;

  :deep(.stk-callout-body) {
    display: flex;
    flex-direction: column;
    gap: 1em;
    text-align: center;
  }
}

.cell-loader {
  font-weight: 400;
  padding: 1.5rem;
}
</style>
